import type { LibrarySketch } from './types';

export const effectSketches: LibrarySketch[] = [
  {
    id: 'effects-glitch-rgb-split',
    name: 'RGB Split Glitch',
    description: 'Splits RGB channels with animated offset and overlays scan lines for a retro glitch aesthetic.',
    category: 'effects',
    tags: ['glitch', 'rgb', 'distortion', 'image-effect', 'retro'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'splitAmount', type: 'float', min: 0.0, max: 0.05, default: 0.01, label: 'Split Amount' },
      { name: 'scanLineIntensity', type: 'float', min: 0.0, max: 1.0, default: 0.3, label: 'Scan Lines' },
      { name: 'speed', type: 'float', min: 0.1, max: 5.0, default: 1.0, label: 'Speed' },
      { name: 'noiseAmount', type: 'float', min: 0.0, max: 1.0, default: 0.2, label: 'Noise' },
    ],
    code: `// @param float splitAmount 0.0 0.05 0.01
// @param float scanLineIntensity 0.0 1.0 0.3
// @param float speed 0.1 5.0 1.0
// @param float noiseAmount 0.0 1.0 0.2

precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_time;
uniform float splitAmount;
uniform float scanLineIntensity;
uniform float speed;
uniform float noiseAmount;
varying vec2 v_texCoord;

float rand(vec2 co) {
  return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec2 uv = v_texCoord;
  float t = u_time * speed;
  float offset = splitAmount * (1.0 + 0.5 * sin(t * 3.0));
  float n = rand(vec2(floor(uv.y * 80.0), floor(t * 10.0))) * noiseAmount;
  float r = texture2D(u_image, uv + vec2(offset + n * 0.01, 0.0)).r;
  float g = texture2D(u_image, uv).g;
  float b = texture2D(u_image, uv - vec2(offset + n * 0.01, 0.0)).b;
  vec3 col = vec3(r, g, b);
  float scanLine = sin(uv.y * u_resolution.y * 1.5) * 0.5 + 0.5;
  col *= 1.0 - scanLineIntensity * (1.0 - scanLine);
  col += n * 0.05;
  gl_FragColor = vec4(col, 1.0);
}`,
  },

  {
    id: 'effects-distortion-melt',
    name: 'Melting Distortion',
    description: 'Creates a melting/dripping distortion effect that warps the input image downward with heat-haze turbulence.',
    category: 'effects',
    tags: ['distortion', 'melt', 'warp', 'image-effect', 'psychedelic'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'meltSpeed', type: 'float', min: 0.1, max: 3.0, default: 0.8, label: 'Melt Speed' },
      { name: 'meltIntensity', type: 'float', min: 0.0, max: 0.1, default: 0.03, label: 'Intensity' },
      { name: 'turbulence', type: 'float', min: 0.0, max: 10.0, default: 4.0, label: 'Turbulence' },
    ],
    code: `// @param float meltSpeed 0.1 3.0 0.8
// @param float meltIntensity 0.0 0.1 0.03
// @param float turbulence 0.0 10.0 4.0

precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_time;
uniform float meltSpeed;
uniform float meltIntensity;
uniform float turbulence;
varying vec2 v_texCoord;

void main() {
  vec2 uv = v_texCoord;
  float t = u_time * meltSpeed;
  float wave1 = sin(uv.x * turbulence + t) * meltIntensity;
  float wave2 = sin(uv.x * turbulence * 2.3 + t * 1.7) * meltIntensity * 0.5;
  float wave3 = cos(uv.x * turbulence * 0.7 + t * 0.5) * meltIntensity * 0.3;
  float drip = (wave1 + wave2 + wave3) * (1.0 - uv.y);
  vec2 distorted = uv + vec2(wave3 * 0.3, drip);
  distorted = clamp(distorted, 0.0, 1.0);
  gl_FragColor = texture2D(u_image, distorted);
}`,
  },

  {
    id: 'effects-pixelate-mosaic',
    name: 'Dynamic Mosaic',
    description: 'Pixelates the input image into a dynamic mosaic grid with animated cell size and optional color quantization.',
    category: 'effects',
    tags: ['pixelate', 'mosaic', 'retro', 'image-effect', '8bit'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'cellSize', type: 'float', min: 2.0, max: 50.0, default: 10.0, label: 'Cell Size' },
      { name: 'animate', type: 'float', min: 0.0, max: 1.0, default: 0.3, label: 'Animation' },
      { name: 'colorLevels', type: 'float', min: 2.0, max: 32.0, default: 6.0, label: 'Color Levels' },
    ],
    code: `// @param float cellSize 2.0 50.0 10.0
// @param float animate 0.0 1.0 0.3
// @param float colorLevels 2.0 32.0 6.0

precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_time;
uniform float cellSize;
uniform float animate;
uniform float colorLevels;
varying vec2 v_texCoord;

void main() {
  vec2 uv = v_texCoord;
  float size = cellSize + sin(u_time * 2.0) * cellSize * animate * 0.5;
  size = max(size, 1.0);
  vec2 pixels = u_resolution / size;
  vec2 snapped = floor(uv * pixels) / pixels;
  snapped += 0.5 / pixels;
  vec4 col = texture2D(u_image, snapped);
  col.rgb = floor(col.rgb * colorLevels) / colorLevels;
  gl_FragColor = col;
}`,
  },

  {
    id: 'effects-feedback-mirror',
    name: 'Feedback Mirror',
    description: 'Creates an infinite feedback mirror effect by blending the input image with a scaled and rotated copy of the previous frame.',
    category: 'effects',
    tags: ['feedback', 'mirror', 'recursive', 'image-effect', 'trippy'],
    runtimeMode: 'p5',
    inputTypes: ['image'],
    params: [
      { name: 'feedbackDecay', type: 'float', min: 0.8, max: 0.99, default: 0.95, label: 'Decay' },
      { name: 'zoomSpeed', type: 'float', min: 0.001, max: 0.05, default: 0.01, label: 'Zoom Speed' },
      { name: 'rotateSpeed', type: 'float', min: 0.0, max: 0.05, default: 0.005, label: 'Rotation' },
    ],
    code: `// @param float feedbackDecay 0.8 0.99 0.95
// @param float zoomSpeed 0.001 0.05 0.01
// @param float rotateSpeed 0.0 0.05 0.005

let feedback;
let inputSrc = null;
let inputImg = null;
function setup() {
  createCanvas(genie.width, genie.height);
  feedback = createGraphics(width, height);
  imageMode(CENTER);
}
function draw() {
  const decay = genie.params.feedbackDecay ?? 0.95;
  const zoom = 1.0 + (genie.params.zoomSpeed ?? 0.01);
  const rot = genie.params.rotateSpeed ?? 0.005;
  feedback.push();
  feedback.translate(width / 2, height / 2);
  feedback.rotate(rot);
  feedback.scale(zoom);
  feedback.tint(255, decay * 255);
  feedback.imageMode(CENTER);
  feedback.image(feedback, 0, 0);
  feedback.pop();
  // genie.inputImage arrives as a data URL; p5 can only draw a loaded p5.Image.
  if (typeof genie.inputImage === 'string' && genie.inputImage !== inputSrc) {
    inputSrc = genie.inputImage;
    loadImage(inputSrc, (img) => { if (inputSrc === genie.inputImage) inputImg = img; });
  }
  if (inputImg) {
    feedback.push();
    feedback.imageMode(CENTER);
    feedback.tint(255, 60);
    feedback.image(inputImg, width / 2, height / 2, width, height);
    feedback.pop();
  }
  image(feedback, width / 2, height / 2);
}`,
  },

  {
    id: 'effects-halftone',
    name: 'Mono Halftone',
    description: 'Converts the input image into a black-and-white halftone dot pattern with controllable dot size, angle and contrast.',
    category: 'effects',
    tags: ['halftone', 'dots', 'print', 'image-effect', 'comic'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'dotSize', type: 'float', min: 2.0, max: 20.0, default: 6.0, label: 'Dot Size' },
      { name: 'angle', type: 'float', min: 0.0, max: 3.14159, default: 0.785, label: 'Angle' },
      { name: 'contrast', type: 'float', min: 0.5, max: 3.0, default: 1.2, label: 'Contrast' },
    ],
    code: `// @param float dotSize 2.0 20.0 6.0
// @param float angle 0.0 3.14159 0.785
// @param float contrast 0.5 3.0 1.2

precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float dotSize;
uniform float angle;
uniform float contrast;
varying vec2 v_texCoord;

mat2 rotate2d(float a) {
  float s = sin(a), c = cos(a);
  return mat2(c, -s, s, c);
}

void main() {
  vec2 uv = v_texCoord;
  vec4 col = texture2D(u_image, uv);
  float luma = dot(col.rgb, vec3(0.299, 0.587, 0.114));
  luma = pow(luma, 1.0 / contrast);
  vec2 px = gl_FragCoord.xy;
  vec2 rotated = rotate2d(angle) * px;
  vec2 grid = mod(rotated, dotSize) - dotSize * 0.5;
  float dist = length(grid);
  float radius = dotSize * 0.5 * (1.0 - luma);
  float dot = smoothstep(radius + 0.5, radius - 0.5, dist);
  gl_FragColor = vec4(vec3(dot), 1.0);
}`,
  },

  {
    id: 'effects-color-shift',
    name: 'Prismatic Hue Shift',
    description: 'Rotates the image hue over time with a light radial channel split for a prismatic look.',
    category: 'effects',
    tags: ['chromatic', 'aberration', 'hue', 'lens', 'image-effect'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'aberration', type: 'float', min: 0.0, max: 0.02, default: 0.005, label: 'Aberration' },
      { name: 'hueShift', type: 'float', min: 0.0, max: 6.28, default: 0.0, label: 'Hue Shift' },
      { name: 'hueSpeed', type: 'float', min: 0.0, max: 2.0, default: 0.3, label: 'Hue Speed' },
    ],
    code: `// @param float aberration 0.0 0.02 0.005
// @param float hueShift 0.0 6.28 0.0
// @param float hueSpeed 0.0 2.0 0.3

precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_time;
uniform float aberration;
uniform float hueShift;
uniform float hueSpeed;
varying vec2 v_texCoord;

vec3 hueRotate(vec3 c, float a) {
  float cosA = cos(a), sinA = sin(a);
  vec3 k = vec3(0.57735);
  return c * cosA + cross(k, c) * sinA + k * dot(k, c) * (1.0 - cosA);
}

void main() {
  vec2 uv = v_texCoord;
  vec2 center = uv - 0.5;
  float dist = length(center);
  vec2 dir = normalize(center + 0.0001);
  float offset = aberration * dist;
  float r = texture2D(u_image, uv + dir * offset).r;
  float g = texture2D(u_image, uv).g;
  float b = texture2D(u_image, uv - dir * offset).b;
  vec3 col = vec3(r, g, b);
  float h = hueShift + u_time * hueSpeed;
  col = hueRotate(col, h);
  gl_FragColor = vec4(col, 1.0);
}`,
  },
];
